import React, { useState } from 'react';
import { Layout } from '@/components/layout';
import { fmtDate as fmtDateUtil } from '@/lib/dateFormat';
import { useAuthStore, useAuthHeaders } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import {
  History,
  RefreshCw,
  Loader2,
  Clock,
  Store,
  AlertCircle,
} from 'lucide-react';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

interface ShopifyStore {
  id: string;
  nickname: string;
}

interface WpImportLogEntry {
  id: string;
  storeId: string;
  sku: string;
  skus: string;
  title: string | null;
  handle: string;
  shopifyProductId: string | null;
  price: string | null;
  cost: string | null;
  importedAt: string;
  lastReimportAt: string | null;
}

function fmtPrice(v: number | string | null | undefined): string {
  if (v == null || v === '') return '—';
  const n = parseFloat(String(v));
  if (isNaN(n)) return String(v);
  return `$${n.toFixed(2)}`;
}

function fmtDate(iso: string): string {
  return fmtDateUtil(iso);
}

export function WheelProsImportLog() {
  const { token } = useAuthStore();
  const authOpts = useAuthHeaders();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [storeId, setStoreId] = useState('');
  const [reimportingSkus, setReimportingSkus] = useState<Set<string>>(new Set());

  const { data: stores = [] } = useQuery<ShopifyStore[]>({
    queryKey: ['stores'],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/stores`, { headers: authOpts.headers });
      if (!res.ok) throw new Error('Failed to load stores');
      return res.json();
    },
    enabled: !!token,
  });

  React.useEffect(() => {
    if (stores.length >= 1 && !storeId) setStoreId(stores[0].id);
  }, [stores, storeId]);

  const {
    data: importLog = [],
    isLoading: logLoading,
    isError: logError,
  } = useQuery<WpImportLogEntry[]>({
    queryKey: ['wp-import-log', storeId],
    queryFn: async () => {
      if (!storeId) return [];
      const res = await fetch(`${BASE}/api/wheelpros/import-log?storeId=${storeId}`, {
        headers: authOpts.headers,
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.log ?? [];
    },
    enabled: !!token && !!storeId,
    staleTime: 30_000,
  });

  const handleReimport = (entry: WpImportLogEntry) => {
    const skus: string[] = (() => {
      try { return JSON.parse(entry.skus); } catch { return [entry.sku]; }
    })();

    setReimportingSkus(prev => new Set([...prev, entry.sku]));

    fetch(`${BASE}/api/wheelpros/import`, {
      method: 'POST',
      headers: { ...authOpts.headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        storeId,
        skus,
        duplicateHandling: 'overwrite',
        productStatus: 'draft',
      }),
    })
      .then(async res => {
        const reader = res.body?.getReader();
        if (!reader) throw new Error('No response stream');
        const decoder = new TextDecoder();
        let doneReading = false;
        let lastDoneEvent: any = null;
        while (!doneReading) {
          const { value, done } = await reader.read();
          doneReading = done;
          if (value) {
            const text = decoder.decode(value);
            for (const line of text.split('\n')) {
              if (line.startsWith('data: ')) {
                try {
                  const evt = JSON.parse(line.slice(6));
                  if (evt.type === 'done') lastDoneEvent = evt;
                } catch {}
              }
            }
          }
        }
        setReimportingSkus(prev => {
          const s = new Set(prev);
          s.delete(entry.sku);
          return s;
        });
        if (lastDoneEvent?.errorCount > 0) {
          toast({
            title: 'Re-import had errors',
            description: lastDoneEvent.errors?.[0]?.error ?? 'Unknown error',
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Re-imported',
            description: `${entry.title || entry.sku} updated in Shopify`,
          });
          queryClient.invalidateQueries({ queryKey: ['wp-import-log', storeId] });
        }
      })
      .catch(err => {
        setReimportingSkus(prev => {
          const s = new Set(prev);
          s.delete(entry.sku);
          return s;
        });
        toast({
          title: 'Re-import failed',
          description: err.message,
          variant: 'destructive',
        });
      });
  };

  const anyReimporting = reimportingSkus.size > 0;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-display font-bold text-gradient">WP Import Log</h1>
            <p className="text-muted-foreground mt-1">
              All products previously imported from the Wheel Pros catalog.
            </p>
          </div>

          <ShopifyTargetStoreSelector
            stores={stores}
            value={storeId}
            onChange={setStoreId}
            label="Shopify store"
            className="w-56"
          />
        </div>

        {/* No store */}
        {!storeId && !logLoading && (
          <div className="glass-panel rounded-2xl flex flex-col items-center justify-center py-20 text-center">
            <Store className="w-10 h-10 text-slate-600 mb-3" />
            <p className="text-muted-foreground font-medium">No store selected</p>
            <p className="text-slate-600 text-sm mt-1">
              Connect a Shopify store first to see your import history.
            </p>
          </div>
        )}

        {/* Error */}
        {logError && (
          <div className="flex items-center gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            Failed to load import log. Please try refreshing.
          </div>
        )}

        {/* Loading */}
        {logLoading && storeId && (
          <div className="flex items-center gap-3 py-10 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading import history…
          </div>
        )}

        {/* Log table */}
        {!logLoading && storeId && (
          <div className="glass-panel rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border/60">
              <div className="flex items-center gap-2.5">
                <History className="w-4 h-4 text-primary" />
                <span className="font-medium text-sm text-foreground">Import History</span>
                {importLog.length > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-primary/15 text-primary text-xs font-medium">
                    {importLog.length}
                  </span>
                )}
              </div>
              <button
                onClick={() => queryClient.invalidateQueries({ queryKey: ['wp-import-log', storeId] })}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded hover:bg-muted/50"
              >
                <RefreshCw className="w-3 h-3" />
                Refresh
              </button>
            </div>

            {importLog.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-20 text-muted-foreground text-sm">
                <Clock className="w-8 h-8 text-slate-700 mb-1" />
                No products imported yet for this store
              </div>
            ) : (
              <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/60 text-xs text-muted-foreground uppercase tracking-wide">
                      <th className="px-6 py-3 text-left font-medium">Product</th>
                      <th className="px-4 py-3 text-left font-medium">SKU</th>
                      <th className="px-4 py-3 text-right font-medium">Price (RRP)</th>
                      <th className="px-4 py-3 text-right font-medium">Cost (NIP)</th>
                      <th className="px-4 py-3 text-left font-medium">Imported</th>
                      <th className="px-4 py-3 text-left font-medium">Re-imported</th>
                      <th className="px-6 py-3 text-right font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {importLog.map(entry => {
                      const isReimp = reimportingSkus.has(entry.sku);
                      return (
                        <tr
                          key={entry.id}
                          className="hover:bg-muted/50 transition-colors"
                        >
                          <td className="px-6 py-3 max-w-[220px]">
                            <span
                              className="text-foreground font-medium truncate block"
                              title={entry.title ?? entry.handle}
                            >
                              {entry.title || entry.handle}
                            </span>
                            <span className="text-xs text-slate-600 font-mono truncate block">
                              {entry.handle}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">
                            {entry.sku}
                          </td>
                          <td className="px-4 py-3 text-right text-foreground whitespace-nowrap tabular-nums">
                            {entry.price
                              ? fmtPrice(entry.price)
                              : <span className="text-slate-600">—</span>}
                          </td>
                          <td className="px-4 py-3 text-right text-muted-foreground whitespace-nowrap tabular-nums">
                            {entry.cost
                              ? fmtPrice(entry.cost)
                              : <span className="text-slate-600">—</span>}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground whitespace-nowrap text-xs">
                            {fmtDate(entry.importedAt)}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground whitespace-nowrap text-xs">
                            {entry.lastReimportAt
                              ? fmtDate(entry.lastReimportAt)
                              : <span className="text-slate-700">—</span>}
                          </td>
                          <td className="px-6 py-3 text-right">
                            <button
                              disabled={isReimp || anyReimporting}
                              onClick={() => handleReimport(entry)}
                              className={cn(
                                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border',
                                isReimp
                                  ? 'border-primary/30 bg-primary/10 text-primary cursor-wait'
                                  : 'border-border bg-muted/50 text-muted-foreground hover:text-foreground hover:border-border hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed'
                              )}
                              title="Re-import and overwrite this product in Shopify"
                            >
                              {isReimp
                                ? <><Loader2 className="w-3 h-3 animate-spin" /> Re-importing…</>
                                : <><RefreshCw className="w-3 h-3" /> Re-import</>}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
